// New / Edit event form (mockup eventForm(); fields per components/admin/EventFormFields.js).
import React, { useEffect, useRef, useState } from "react";
import Modal, { ModalBody, ModalFooter } from "../../../components/Modal";
import Icon from "../../../components/Icon";
import { Button, Field, Seg } from "../../../components/primitives";
import { compressForEvent, isLocalUri, pickFiles, releaseUri } from "../../../lib/files";
import { createEvent, editEvent } from "./eventActions";
import { msg, startToInputs } from "./eventUtils";

const FREQS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
];

function todayInput() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function initialForm(event) {
  if (!event) {
    return { title: "", description: "", date: "", time: "", location: "", is_public: true, image_url: "", is_recurring: false, recurrence_frequency: "weekly", recurrence_count: "4" };
  }
  const { date, time } = startToInputs(event.start_date);
  return {
    title: event.title || "",
    description: event.description || "",
    date,
    time,
    location: event.location || "",
    is_public: event.is_public !== false,
    image_url: event.image_url || "",
    is_recurring: false,
    recurrence_frequency: "weekly",
    recurrence_count: "4",
  };
}

export default function EventFormModal({ club, event, uid, onClose, onCreated, onEdited }) {
  const editing = !!event;
  const [form, setForm] = useState(() => initialForm(event));
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState("");
  const blobRef = useRef(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const aliveRef = useRef(false);
  const submittingRef = useRef(false);
  // Release the picked preview blob on unmount unless a submit owns it (its finally releases it then).
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (!submittingRef.current && blobRef.current) releaseUri(blobRef.current);
    };
  }, []);

  const takeFile = async (file) => {
    if (!file || submittingRef.current) return; // a submitted photo is owned by the save
    setPhotoBusy(true);
    setError("");
    try {
      const uri = await compressForEvent(file);
      if (blobRef.current) releaseUri(blobRef.current);
      blobRef.current = uri;
      set({ image_url: uri });
    } catch (e) {
      setError(msg(e, "Could not process this photo."));
    } finally {
      setPhotoBusy(false);
    }
  };
  const pick = async () => takeFile((await pickFiles({ accept: "image/*" }))[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (busy || photoBusy) return;
    const f = Array.from(e.dataTransfer?.files || []).find((x) => /^image\//.test(x.type));
    takeFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy || photoBusy || submittingRef.current) return; // Enter while compressing would drop the photo
    submittingRef.current = true;
    setBusy(true);
    setError("");
    try {
      if (editing) {
        const res = await editEvent({ form, event, uid });
        onEdited(event.id, res.patch, res.imageUpload);
      } else {
        const res = await createEvent({ form, club, uid });
        if (blobRef.current) releaseUri(blobRef.current); // create awaited the upload
        blobRef.current = null;
        onCreated(res);
      }
      blobRef.current = null; // edit: the blob may still be uploading in the background
    } catch (err) {
      if (err?.created?.length) onCreated({ events: err.created, extra: err.created.length - 1, partial: true });
      const hint = /upload|REST API|network/i.test(err?.message || "") ? " You can also create it without a photo and add one with Edit." : "";
      if (aliveRef.current) {
        setError(`${msg(err, "Failed to save event")}${hint}`);
        setBusy(false);
      }
    } finally {
      submittingRef.current = false;
      if (!aliveRef.current && blobRef.current) { releaseUri(blobRef.current); blobRef.current = null; }
    }
  };

  const hasImg = !!form.image_url;
  const coverStyle = hasImg ? { backgroundImage: `url("${form.image_url}")` } : undefined;

  return (
    <Modal title={editing ? "Edit event" : "New event"} onClose={onClose} lock={busy} testId="eventForm">
      <ModalBody as="form" onSubmit={submit}>
        {/* Every control is locked while saving or compressing: an edit then would be lost or revoke the submitted photo. */}
        <fieldset disabled={busy || photoBusy} style={{ display: "contents" }}>
        <button
          type="button"
          className={`ev-cover${hasImg ? " has" : ""}${drag ? " drag" : ""}`}
          style={coverStyle}
          onClick={pick}
          data-testid="eventForm.cover"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          aria-label={hasImg ? "Change cover photo" : "Add a cover photo"}
        >
          {!hasImg && (
            <span className="ev-cover-empty">
              <Icon name="image" size={26} />
              <b>{photoBusy ? "Processing…" : "Add a cover photo"}</b>
              <span className="tiny">Click or drag an image here</span>
            </span>
          )}
        </button>
        {hasImg && (
          <Button variant="ghost" size="sm" style={{ alignSelf: "flex-start" }} data-testid="eventForm.removeCover" onClick={() => { if (isLocalUri(form.image_url)) { releaseUri(form.image_url); blobRef.current = null; } set({ image_url: "" }); }}>
            Remove photo
          </Button>
        )}
        <Field label="Event title" value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Event Title" required data-testid="eventForm.title" />
        <Field label="Description" as="textarea" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Description" data-testid="eventForm.description" />
        <Field label="Location" value={form.location} onChange={(e) => set({ location: e.target.value })} placeholder="Location" required data-testid="eventForm.location" />
        <div className="grid g2">
          <Field label="Date" type="date" min={editing ? undefined : todayInput()} value={form.date} onChange={(e) => set({ date: e.target.value })} required data-testid="eventForm.date" />
          <Field label="Start time" type="time" value={form.time} onChange={(e) => set({ time: e.target.value })} required data-testid="eventForm.time" />
        </div>
        <div className="field">
          <span>Visibility</span>
          <Seg
            label="Visibility"
            testId="eventForm.visibility"
            value={form.is_public ? "public" : "private"}
            onChange={(v) => set({ is_public: v === "public" })}
            options={[{ value: "public", label: "Public" }, { value: "private", label: "Private" }]}
          />
        </div>
        {!editing && (
          <>
            <label className="row ev-row-box">
              <div>
                <b>Recurring event</b>
                <div className="meta">Repeat this event on a schedule</div>
              </div>
              <span className="switch"><input type="checkbox" checked={form.is_recurring} onChange={(e) => set({ is_recurring: e.target.checked })} aria-label="Recurring event" data-testid="eventForm.recurring" /></span>
            </label>
            {form.is_recurring && (
              <div className="grid g2">
                <div className="field">
                  <span>Frequency</span>
                  <Seg label="Frequency" value={form.recurrence_frequency} onChange={(v) => set({ recurrence_frequency: v })} options={FREQS} testId="eventForm.frequency" />
                </div>
                <Field label="Number of occurrences" type="number" min={2} max={52} value={form.recurrence_count} onChange={(e) => set({ recurrence_count: e.target.value })} data-testid="eventForm.occurrences" />
              </div>
            )}
          </>
        )}
        </fieldset>
        {error && <div className="err-text" role="alert">{error}</div>}
        <ModalFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="eventForm.cancel">Cancel</Button>
          <Button type="submit" variant="pri" disabled={busy || photoBusy} data-testid="eventForm.submit">{busy ? "Saving…" : editing ? "Save changes" : "Create event"}</Button>
        </ModalFooter>
      </ModalBody>
    </Modal>
  );
}
